import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Trash2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';

export default function CartPage() {
  const { items, removeFromCart, getTotal } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const subtotal = getTotal();
  // Calculate total shipping based on individual artwork shipping rates
  const shipping = items.reduce((sum, item) => sum + (item.shipping_rate ?? 15.0), 0);
  const total = subtotal + shipping;

  const handleCheckout = () => {
    navigate('/checkout');
  };

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="cart-page">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-8 hover:translate-x-[-4px] transition-transform"
          data-testid="cart-back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          BACK
        </Button>

        <h1 className="font-serif text-4xl md:text-5xl tracking-tight mb-12" data-testid="cart-title">
          Shopping Cart
        </h1>

        {items.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground mb-6" data-testid="empty-cart-message">
              Your cart is empty
            </p>
            <Link to="/gallery">
              <Button className="rounded-full" data-testid="continue-shopping-btn">
                CONTINUE SHOPPING
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <div className="space-y-6">
                {items.map((item) => (
                  <div
                    key={item.artwork_id}
                    className="flex gap-6 p-6 border border-border rounded-lg"
                    data-testid={`cart-item-${item.artwork_id}`}
                  >
                    <Link to={`/artwork/${item.artwork_id}`} className="flex-shrink-0">
                      <img
                        src={item.artwork_image}
                        alt={item.artwork_title}
                        className="w-32 h-40 object-cover"
                      />
                    </Link>
                    <div className="flex-1">
                      <Link to={`/artwork/${item.artwork_id}`}>
                        <h3 className="font-serif text-xl mb-2 hover:text-accent transition-colors">
                          {item.artwork_title}
                        </h3>
                      </Link>
                      <p className="text-lg font-medium" data-testid={`cart-item-price-${item.artwork_id}`}>
                        ${item.price.toFixed(2)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeFromCart(item.artwork_id)}
                      className="hover:text-destructive"
                      data-testid={`remove-cart-item-${item.artwork_id}`}
                    >
                      <Trash2 className="h-5 w-5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="border border-border rounded-lg p-8 sticky top-32">
                <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
                  ORDER SUMMARY
                </h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Shipping</span>
                    <span data-testid="cart-shipping">{shipping === 0 ? 'Free' : `$${shipping.toFixed(2)}`}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tax</span>
                    <span className="text-xs text-muted-foreground italic" data-testid="cart-tax">Calculated at payment</span>
                  </div>
                  <div className="border-t border-border pt-4">
                    <div className="flex justify-between text-lg font-medium">
                      <span>Total</span>
                      <span data-testid="cart-total">${total.toFixed(2)}<span className="text-xs font-normal text-muted-foreground ml-1">+ tax</span></span>
                    </div>
                  </div>
                </div>

                <Button
                  size="lg"
                  onClick={handleCheckout}
                  className="w-full rounded-full px-8 py-6 text-sm uppercase tracking-widest"
                  data-testid="proceed-checkout-btn"
                >
                  PROCEED TO CHECKOUT
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}